/**
 * 施術録検索 search form body (no outer card — parent wraps in CollapsibleCard).
 * Row 1: 提出年月 / 施術年月 (labels above, month picker)
 * Row 2: ○受診者番号 [textbox]  ○受診者名 [textbox]
 * Row 3: 並び順 [radios inline]                      [検索 button right]
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  AppButton,
  AppCol,
  AppForm,
  AppFormLabel,
  AppRadio,
  AppRadioGroup,
  AppRow,
  AppTextField,
} from '@/components/atoms';
import { RA2NDatePicker } from '@/components/organisms';
import { mbConvertKana } from '@/utils/japaneseText';
import {
  PATIENT_NAME_MAX_LENGTH,
  SEARCH_RADIO_NAME,
  SEARCH_RADIO_SERIAL,
  SERIAL_NUMBER_MAX_LENGTH,
  SORT_BY_KANA,
  SORT_BY_SERIAL,
} from '../constants';

export interface TreatmentRecordSearchFormValues {
  searchYearmonth: string;
  treatmentYearmonth: string;
  searchRadio: '' | typeof SEARCH_RADIO_SERIAL | typeof SEARCH_RADIO_NAME;
  serialNumber: string;
  patientName: string;
  sortRadio: typeof SORT_BY_SERIAL | typeof SORT_BY_KANA;
}

interface Props {
  initialValues: TreatmentRecordSearchFormValues;
  onSearch: (values: TreatmentRecordSearchFormValues) => void;
  onEmptyYearmonthError: () => void;
  isLoading: boolean;
  totalCount?: number;
}

const TreatmentRecordSearchForm: React.FC<Props> = ({
  initialValues,
  onSearch,
  onEmptyYearmonthError,
  isLoading,
  totalCount = 0,
}) => {
  const [form, setForm] = useState<TreatmentRecordSearchFormValues>(initialValues);

  useEffect(() => {
    setForm(initialValues);
  }, [initialValues]);

  const updateField = useCallback(
    <K extends keyof TreatmentRecordSearchFormValues>(
      key: K,
      value: TreatmentRecordSearchFormValues[K]
    ) => {
      setForm(prev => ({ ...prev, [key]: value }));
    },
    []
  );

  const handleSearch = useCallback(() => {
    if (!form.searchYearmonth && !form.treatmentYearmonth) {
      onEmptyYearmonthError();
      return;
    }
    onSearch(form);
  }, [form, onSearch, onEmptyYearmonthError]);

  return (
    <div className="p-3 mb-4 rounded bg-gray-50">
      <AppForm layout="vertical">
        <AppRow gutter={[16, 16]}>
          {/* Row 1: 提出年月 / 施術年月 (inline label + input) */}
          <AppCol xs={24}>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
              <div className="flex items-center gap-2">
                <AppFormLabel label="提出年月" className="!mb-0 whitespace-nowrap" />
                <RA2NDatePicker
                  picker="month"
                  value={form.searchYearmonth}
                  onEnter={handleSearch}
                  onChange={val => updateField('searchYearmonth', val)}
                  placeholder="元号00年00月"
                  className="w-56"
                />
              </div>
              <div className="flex items-center gap-2">
                <AppFormLabel label="施術年月" className="!mb-0 whitespace-nowrap" />
                <RA2NDatePicker
                  picker="month"
                  value={form.treatmentYearmonth}
                  onEnter={handleSearch}
                  onChange={val => updateField('treatmentYearmonth', val)}
                  placeholder="年月指定"
                  className="w-56"
                />
              </div>
            </div>
          </AppCol>

          {/* Row 2: ○受診者番号 [textbox]  ○受診者名 [textbox] */}
          <AppCol xs={24}>
            <AppRadioGroup
              value={form.searchRadio}
              onChange={e => {
                const val = e.target.value as TreatmentRecordSearchFormValues['searchRadio'];
                updateField('searchRadio', val);
                if (val !== SEARCH_RADIO_SERIAL) updateField('serialNumber', '');
                if (val !== SEARCH_RADIO_NAME) updateField('patientName', '');
              }}
            >
              <div className="flex flex-wrap items-center gap-x-8 gap-y-2">
                <div className="flex items-center gap-2">
                  <AppRadio value={SEARCH_RADIO_SERIAL}>受診者番号</AppRadio>
                  <AppTextField
                    value={form.serialNumber}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      updateField('serialNumber', e.target.value)
                    }
                    onPressEnter={handleSearch}
                    maxLength={SERIAL_NUMBER_MAX_LENGTH}
                    disabled={form.searchRadio !== SEARCH_RADIO_SERIAL}
                    className="w-48"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <AppRadio value={SEARCH_RADIO_NAME}>受診者名</AppRadio>
                  <AppTextField
                    value={form.patientName}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      updateField('patientName', e.target.value)
                    }
                    onBlur={() => {
                      // Normalize half-width → full-width (space + alpha + numeric + katakana) on blur.
                      const v = form.patientName;
                      if (v && v.trim() !== '') {
                        updateField('patientName', mbConvertKana(v, 'SANK'));
                      }
                    }}
                    onPressEnter={handleSearch}
                    maxLength={PATIENT_NAME_MAX_LENGTH}
                    disabled={form.searchRadio !== SEARCH_RADIO_NAME}
                    className="w-72"
                  />
                </div>
              </div>
            </AppRadioGroup>
          </AppCol>

          {/* Row 3: 並び順 (inline) + 検索 button (right) */}
          <AppCol xs={24}>
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-gray-700 whitespace-nowrap">並び順</span>
                <AppRadioGroup
                  value={form.sortRadio}
                  onChange={e =>
                    updateField(
                      'sortRadio',
                      e.target.value as TreatmentRecordSearchFormValues['sortRadio']
                    )
                  }
                >
                  <AppRadio value={SORT_BY_SERIAL}>受診者番号</AppRadio>
                  <AppRadio value={SORT_BY_KANA}>仮名</AppRadio>
                </AppRadioGroup>
              </div>
              <div className="flex items-center gap-3">
                <AppButton
                  onClick={handleSearch}
                  type="primary"
                  loading={isLoading}
                  icon={<i className="fa fa-search" />}
                >
                  検索
                </AppButton>
                <span className="px-3 py-1 text-sm font-medium leading-tight text-center text-black rounded-md bg-amber-400">
                  該当件数 {totalCount}
                </span>
              </div>
            </div>
          </AppCol>
        </AppRow>
      </AppForm>
    </div>
  );
};

export default TreatmentRecordSearchForm;
